import { Drawer } from "antd";
import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useForm } from "antd/lib/form/Form";
import GenericFormComponent from "../../../shared/components/generic-form/generic-form.component";
import { RootState } from "../../../shared/types/common";
import { showNotification } from "../../../shared/utils/utils";
import { Client } from "../../../core/api-client";
import {
  createUser,
  getRoles,
  updateUserById,
} from "../../../core/store/reducers/user.slice";
import { API_GET_STAFF_LIST } from "../../../shared/routes/private.routes";

interface Props {
  onClose: () => void;
  visible: boolean;
  selectedEntity?: any;
  onSubmit?: () => void;
}

const CreateEditUserComponent = ({
  onClose,
  visible,
  selectedEntity = null,
  onSubmit = () => {
    return;
  },
}: Props) => {
  const dispatch = useDispatch();
  const [form] = useForm();
  const { screenType } = useSelector((state: RootState) => state.settings);
  const { roleList } = useSelector((state: RootState) => state.userManagement);
  const [staffList, setStaffList] = useState<any[]>([]);

  useEffect(() => {
    dispatch(getRoles());
  }, [dispatch]);

  const fetchStaffList = async () => {
    const response = await Client.post(
      API_GET_STAFF_LIST,
      { staffStatusId: ["ACTIVE", "NOTICE PERIOD"] },
      { params: { page: 1, limit: 10000 } }
    );
    if (response?.data?.data) {
      setStaffList(response.data.data);
    } else {
      setStaffList([]);
    }
  };

  useEffect(() => {
    fetchStaffList();
  }, []);

  useEffect(() => {
    if (selectedEntity) {
      form.setFieldsValue({
        staffId: selectedEntity?.staffId,
        email: selectedEntity?.email,
        role: selectedEntity?.role?.id,
        status: selectedEntity?.status,
      });
    }
  }, [form, selectedEntity]);

  const onFinish = (values: any) => {
    const selectedStaff = staffList.find((staff) => staff?.staffId === values?.staffId);
    const payload = {
      staffId: values?.staffId,
      fullName: selectedStaff?.staffName || selectedEntity?.fullName,
      email: selectedStaff?.email || values?.email,
      role: values?.role,
      status: values?.status,
    };

    if (selectedEntity) {
      dispatch(updateUserById({ userId: selectedEntity?.id, body: payload })).then(
        (val: any) => {
          if (val?.payload?.data) {
            showNotification("User Updated Successfully", "success");
            onSubmit();
          }
        }
      );
      return;
    }

    dispatch(createUser(payload)).then((val: any) => {
      if (val?.payload?.data) {
        showNotification("New User Created Successfully", "success");
        onSubmit();
      }
    });
  };

  const roleOptions = roleList.map((role: any) => ({
    value: role?.id,
    label: role?.name,
  }));

  const staffOptions = useMemo(
    () =>
      staffList.map((staff: any) => ({
        value: staff?.staffId,
        label: staff?.staffName,
      })),
    [staffList]
  );

  const handleStaffChange = (staffId: string) => {
    const selectedStaff = staffList.find((staff) => staff?.staffId === staffId);
    form.setFieldsValue({
      email: selectedStaff?.email || undefined,
    });
  };

  const colSpan = screenType.isPortrait ? 24 : 12;
  const formProps = {
    form,
    name: "create-edit-user",
    heading: selectedEntity ? "Edit User Details" : "Add New User",
    initialValues: {
      status: true,
    },
    formElements: [
      {
        name: "staffId",
        colSpan,
        label: "Full Name",
        placeholder: "Select Full Name",
        type: "Select",
        showSearch: true,
        allowClear: true,
        options: staffOptions,
        onChange: handleStaffChange,
        rules: [{ required: true, message: "Please select Full Name" }],
      },
      {
        name: "email",
        colSpan,
        label: "Email",
        placeholder: "Enter Email",
        type: "Input",
        allowClear: true,
        disabled: true,
        rules: [
          { required: true, message: "Please enter Email" },
          { type: "email", message: "Please enter a valid Email" },
        ],
      },
      {
        name: "role",
        label: "Role",
        placeholder: "Select Role",
        type: "Select",
        colSpan,
        showSearch: true,
        allowClear: true,
        options: roleOptions,
        rules: [{ required: true, message: "Please select Role" }],
      },
      {
        name: "status",
        label: "Status",
        placeholder: "Status",
        type: "Select",
        colSpan,
        showSearch: true,
        options: [
          { value: true, label: "ACTIVE" },
          { value: false, label: "INACTIVE" },
        ],
        rules: [{ required: true, message: "Please select Status" }],
      },
    ],
    extraButtons: [
      {
        htmlType: "button",
        colSpan: 5,
        loading: false,
        submitButtonText: "Cancel",
        onClick: onClose,
      },
      {
        htmlType: "button",
        colSpan: 5,
        loading: false,
        submitButtonText: "Clear",
        type: "clearForm",
      },
      {
        htmlType: "submit",
        colSpan: 5,
        loading: false,
        submitButtonText: "Save",
        submitButtonType: "primary",
      },
    ],
    onFinish,
    onFinishFailed: null,
    onValuesChange: () => {
      return;
    },
  };

  return (
    <Drawer
      maskClosable={false}
      title={<strong>{formProps.heading}</strong>}
      className="form-drawer"
      width={screenType.isPortrait ? "90%" : screenType.isLg || screenType.isXl ? 720 : 500}
      onClose={onClose}
      open={visible}
    >
      <GenericFormComponent {...formProps} />
    </Drawer>
  );
};

export default CreateEditUserComponent;


